import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useRef, useState } from "react";
import { usePostOnboardingDataMutation } from "../../../store/api/onboardingApi";
import { useAppSelector } from "../../../hooks/store-hooks";
import { ONBOARDING_PAGE_ID } from "../../../constants";
import { useNavigate } from "react-router-dom";
import { ONBOARDING_PERSONAL_INFO_SCHEMA } from "../../../helpers/validation-schema";
import { ONBOARDING_ROUTE_MAPPING } from "../../../constants/routes";
import PersonalDetailsComp from "../../../components/onboarding/personal-details";
import AuthLayout from "../../../components/layouts/auth";
import Modal from "../../../components/common/modals";

type MobileFormData = {
  first_name: string;
  last_name: string;
  username: string;
  gender: string;
  dob: string;
};

const UserPersonalDetails = () => {
  const [postOnboardingData] = usePostOnboardingDataMutation();

  const { data: onboardingData }: any = useAppSelector(
    (state) => state.onboarding || {},
  );
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(true);
  const [locationConsent, setlocationConsent] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const screenName = ONBOARDING_PAGE_ID.USER_PERSONAL_DETAILS;
  const navigate = useNavigate();
  const methods = useForm<MobileFormData>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: yupResolver(ONBOARDING_PERSONAL_INFO_SCHEMA),
    defaultValues: {
      first_name: "",
      last_name: "",
      username: "",
      gender: "",
      dob: "",
    },
  });

  const {
    handleSubmit,
    formState: { errors },
    setValue,
  } = methods;

  useEffect(() => {
    if (onboardingData?.page_id === screenName) {
      const pageData = onboardingData?.page_data || [];
      pageData.forEach((itm: any) => {
        setValue(itm?.field_name, itm?.value);
      });
    }
  }, [onboardingData]);

  useEffect(() => {
    if ("permissions" in navigator && "geolocation" in navigator) {
      // if (isLocationModalOpen) {
      //   trackEvent(MixpanelEvent.ONBOARDING_LOC_PERM_MODAL_VIEW, {});
      // }

      navigator.permissions
        .query({ name: "geolocation" })
        .then((permissionStatus) => {
          if (permissionStatus.state === "granted") {
            console.log("Location permission already granted");
            setlocationConsent(true);
            setIsLocationModalOpen(false);
            // trackEvent(MixpanelEvent.ONBOARDING_LOC_PERM_MODAL_ALLOW, {});
          } else if (permissionStatus.state === "prompt") {
            navigator.geolocation.getCurrentPosition(
              function (position) {
                console.log("Location access granted", position);
                setlocationConsent(true);
                setIsLocationModalOpen(false);
                // trackEvent(MixpanelEvent.ONBOARDING_LOC_PERM_MODAL_ALLOW, {});
              },
              function (error) {
                console.error("Location access denied", error);
                setIsLocationModalOpen(false);
                // trackEvent(MixpanelEvent.ONBOARDING_LOC_PERM_MODAL_DISMISS, {});
              },
            );
          } else if (permissionStatus.state === "denied") {
            setIsLocationModalOpen(false);
            // trackEvent(MixpanelEvent.ONBOARDING_LOC_PERM_MODAL_DISMISS, {});
          }

          permissionStatus.onchange = () => {
            if (
              permissionStatus.state === "granted" ||
              permissionStatus.state === "denied"
            ) {
              setIsLocationModalOpen(false);
            }
          };
        });
    } else {
      setIsLocationModalOpen(false);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node) &&
        isLocationModalOpen
      ) {
        setIsLocationModalOpen(false);
      }
    };

    if (isLocationModalOpen) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isLocationModalOpen]);

  const onSubmit = async (data: MobileFormData) => {
    if (errors?.username?.message) {
      return;
    }
    const response = await postOnboardingData({
      page_id: ONBOARDING_PAGE_ID.USER_PERSONAL_DETAILS,
      ...data,
      location: { consent: locationConsent },
    });
    if (response?.data?.success) {
      const nextPageId: any = response.data?.data?.nextPage || "";
      navigate(ONBOARDING_ROUTE_MAPPING?.[nextPageId]);
    }
  };

  return (
    <>
      <Modal
        isOpen={isLocationModalOpen}
        closeOnOverlayClick={true}
        customClass="bg-[#2C2C2E] text-white !w-[330px]"
        variant="middle"
        onClose={() => setIsLocationModalOpen(false)}
        blurBg={true}
      >
        <div ref={modalRef} className="text-center p-6">
          <h2 className="font-bold mb-2 text-white text-[28px]">
            Grant Location
          </h2>
          <p className="text-gray-400 mb-6  text-[17px]">
            Enable location services to receive a personalized 1% Club
            experience tailored just for you
          </p>

          <div className="bg-[#1C1C1E] rounded-lg p-4 mb-6 flex flex-row items-center">
            <h3 className="text-[24px] font-bold mb-2 w-1/2">
              Get access to exclusive events in your area
            </h3>
            <div className="w-1/2">
              <img
                src="/assets/location1.png"
                alt="Event"
                className="w-[260px] h-[162px] object-cover rounded-lg float-left"
              />
            </div>
          </div>

          <div className="bg-[#1C1C1E] rounded-lg p-4 mb-6 flex flex-row items-center">
            <h3 className="text-[24px] font-bold mb-2 w-1/2">
              City-specific chat groups based on location
            </h3>
            <div className="w-1/2 flex justify-center">
              <img
                src="/assets/location2.png"
                alt="Event"
                className="w-[83px] h-[140px] object-cover rounded-lg float-left"
              />
              <img
                src="/assets/location3.png"
                alt="Event"
                className="w-[140px] h-[142px] object-cover rounded-lg float-left"
              />
            </div>
          </div>
        </div>
      </Modal>
      <FormProvider {...methods}>
        <AuthLayout screenName={screenName}>
          <form
            className="flex w-full h-full"
            onSubmit={handleSubmit(onSubmit)}
          >
            <PersonalDetailsComp />
          </form>
        </AuthLayout>
      </FormProvider>
    </>
  );
};

export default UserPersonalDetails;
